import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Trophy, BookOpen, Wrench, Rocket, ChevronRight, Check, X as XIcon,
  Loader2, Flame, Crown, Shield, Star, Zap, Timer, RefreshCw,
  ArrowRight, Copy, Users, Target, Vote, FileCode, Globe, Hash
} from 'lucide-react';
import axios from 'axios';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  Diamond: { color: 'from-cyan-400 to-blue-500', border: 'border-cyan-500/30', text: 'text-cyan-400', icon: Crown, bg: 'bg-cyan-500/10' },
  Gold: { color: 'from-yellow-400 to-amber-500', border: 'border-yellow-500/30', text: 'text-yellow-400', icon: Trophy, bg: 'bg-yellow-500/10' },
  Silver: { color: 'from-gray-300 to-gray-400', border: 'border-gray-400/30', text: 'text-gray-300', icon: Shield, bg: 'bg-gray-400/10' },
};

// ── Phase progress component ──
function PhaseCard({ phase, title, icon: Icon, description, complete, locked, active, children }) {
  const [expanded, setExpanded] = useState(active);
  return (
    <Card className={`bg-[#07070f] transition-all ${complete ? 'border-green-500/20' : active ? 'border-purple-500/30' : 'border-white/5 opacity-60'}`}
      data-testid={`phase-${phase}`}
    >
      <CardHeader className="pb-2 cursor-pointer" onClick={() => !locked && setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${complete ? 'bg-green-500/10' : active ? 'bg-purple-500/10' : 'bg-white/5'}`}>
              {complete ? <Check className="w-4 h-4 text-green-400" /> : <Icon className={`w-4 h-4 ${active ? 'text-purple-400' : 'text-gray-500'}`} />}
            </div>
            <div>
              <CardTitle className="text-sm flex items-center gap-2">
                Phase {phase}: {title}
                {complete && <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px]">Complete</Badge>}
                {locked && <Badge variant="outline" className="text-[10px] text-gray-500">Locked</Badge>}
              </CardTitle>
              <p className="text-[11px] text-gray-500 mt-0.5">{description}</p>
            </div>
          </div>
          {!locked && <ChevronRight className={`w-4 h-4 text-gray-500 transition-transform ${expanded ? 'rotate-90' : ''}`} />}
        </div>
      </CardHeader>
      <AnimatePresence>
        {expanded && !locked && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}>
            <CardContent className="pt-0">{children}</CardContent>
          </motion.div>
        )}
      </AnimatePresence>
    </Card>
  );
}

// ── Quiz Component ──
function ScholarQuiz({ token, onPass, profile }) {
  const { toast } = useToast();
  const [quiz, setQuiz] = useState(null);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const startQuiz = async () => {
    setLoading(true);
    setResult(null);
    setAnswers({});
    try {
      const { data } = await axios.post(`${API}/api/genesis-quest/quiz/start`, {}, { headers });
      setQuiz(data);
    } catch (err) {
      toast({ title: 'Failed to start quiz', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const submitQuiz = async () => {
    if (Object.keys(answers).length < quiz.questions.length) {
      toast({ title: 'Answer all questions', description: `${Object.keys(answers).length}/${quiz.questions.length} answered`, variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/genesis-quest/quiz/submit`, {
        quiz_id: quiz.quiz_id,
        answers,
      }, { headers });
      setResult(data);
      if (data.passed) {
        toast({ title: 'Scholar Quiz Passed!', description: `+${data.vxp_earned} vXP earned` });
        onPass();
      }
    } catch (err) {
      toast({ title: 'Submission failed', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (profile?.phase1_passed) {
    return (
      <div className="p-4 rounded-lg bg-green-500/5 border border-green-500/20 flex items-center gap-3">
        <Check className="w-5 h-5 text-green-400" />
        <div>
          <p className="text-sm text-green-300 font-semibold">Scholar Quiz Passed</p>
          <p className="text-[11px] text-gray-500">Best score: {Math.round((profile.phase1_best_score || 1) * 100)}% | Attempts: {profile.phase1_attempts}</p>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div className="space-y-4">
        <div className={`p-4 rounded-lg ${result.passed ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'} border`}>
          <p className={`text-lg font-bold ${result.passed ? 'text-green-400' : 'text-red-400'}`}>
            {result.passed ? 'PASSED!' : 'NOT YET'} — {result.score} ({result.percentage}%)
          </p>
          <p className="text-xs text-gray-400 mt-1">{result.message}</p>
        </div>
        {result.results.map(r => (
          <div key={r.id} className={`flex items-center gap-2 text-xs p-2 rounded ${r.is_correct ? 'bg-green-500/5' : 'bg-red-500/5'}`}>
            {r.is_correct ? <Check className="w-3 h-3 text-green-400" /> : <XIcon className="w-3 h-3 text-red-400" />}
            <span className="text-gray-300">{r.id}: Your answer: {r.your_answer || 'N/A'}</span>
            {!r.is_correct && <span className="text-gray-500 ml-auto">Correct: {r.correct_answer}</span>}
          </div>
        ))}
        {!result.passed && (
          <Button onClick={startQuiz} variant="outline" className="w-full border-white/10 gap-2" data-testid="retry-quiz-btn">
            <RefreshCw className="w-4 h-4" /> Try Again
          </Button>
        )}
      </div>
    );
  }

  if (!quiz) {
    return (
      <div className="space-y-3">
        <p className="text-xs text-gray-400">10 randomized questions on Velocity Score, Anti-Fragile Reserve, Burn-to-Boost, and more. You must score <span className="text-cyan-400 font-semibold">100%</span> to pass.</p>
        {profile?.phase1_attempts > 0 && (
          <p className="text-[11px] text-yellow-400">Previous attempts: {profile.phase1_attempts} | Best: {Math.round((profile.phase1_best_score || 0) * 100)}%</p>
        )}
        <Button onClick={startQuiz} disabled={loading} className="w-full bg-purple-600 hover:bg-purple-500 gap-2" data-testid="start-quiz-btn">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookOpen className="w-4 h-4" />}
          Start Scholar Quiz
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-xs text-gray-400">
        <span>{Object.keys(answers).length}/{quiz.questions.length} answered</span>
        <Badge variant="outline" className="text-[10px]">{quiz.quiz_id}</Badge>
      </div>
      <Progress value={(Object.keys(answers).length / quiz.questions.length) * 100} className="h-1" />

      {quiz.questions.map((q, i) => (
        <div key={q.id} className="p-4 rounded-lg bg-[#0a0a14] border border-white/5" data-testid={`quiz-question-${q.id}`}>
          <p className="text-sm text-white mb-3">
            <span className="text-gray-500 mr-2">Q{i + 1}.</span>
            {q.question}
          </p>
          <div className="space-y-2">
            {q.options.map(opt => (
              <button
                key={opt.key}
                onClick={() => setAnswers(prev => ({ ...prev, [q.id]: opt.key }))}
                className={`w-full text-left p-3 rounded-lg text-xs transition-all border ${
                  answers[q.id] === opt.key
                    ? 'bg-purple-500/10 border-purple-500/30 text-white'
                    : 'bg-black/30 border-white/5 text-gray-400 hover:border-white/10 hover:text-gray-300'
                }`}
                data-testid={`answer-${q.id}-${opt.key}`}
              >
                <span className="font-mono mr-2 text-gray-500">{opt.key}.</span>
                {opt.text}
              </button>
            ))}
          </div>
        </div>
      ))}

      <Button onClick={submitQuiz} disabled={submitting || Object.keys(answers).length < quiz.questions.length}
        className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 gap-2" data-testid="submit-quiz-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        Submit Quiz ({Object.keys(answers).length}/{quiz.questions.length})
      </Button>
    </div>
  );
}

// ── Simulation Component ──
function BlackSwanSimulation({ token, onComplete, profile }) {
  const { toast } = useToast();
  const [action, setAction] = useState('');
  const [allocation, setAllocation] = useState(40);
  const [submitting, setSubmitting] = useState(false);

  if (profile?.simulation_complete) {
    return (
      <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20 flex items-center gap-2">
        <Check className="w-4 h-4 text-green-400" />
        <span className="text-xs text-green-300">Vulture Multiplier earned</span>
      </div>
    );
  }

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/genesis-quest/simulation/submit`, {
        rebalance_action: action,
        reserve_allocation: allocation,
      }, { headers: { Authorization: `Bearer ${token}` } });
      if (data.success) {
        toast({ title: 'Simulation Complete!', description: `+${data.vxp_earned} vXP` });
        onComplete();
      } else {
        toast({ title: 'Simulation Failed', description: data.message, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Error', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 mt-3">
      <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/20">
        <p className="text-xs text-red-300 font-semibold">Scenario: Bitcoin drops 20% in 4 hours</p>
        <p className="text-[11px] text-gray-400 mt-1">The Volatility Reserve has activated crisis mode. How do you respond?</p>
      </div>
      <div className="space-y-2">
        {[
          { key: 'withdraw', label: 'Withdraw everything to safety' },
          { key: 'hold', label: 'Hold position and wait it out' },
          { key: 'provide_liquidity', label: 'Provide liquidity to earn the Vulture Multiplier' },
        ].map(opt => (
          <button key={opt.key} onClick={() => setAction(opt.key)}
            className={`w-full text-left p-3 rounded-lg text-xs border transition-all ${
              action === opt.key ? 'bg-purple-500/10 border-purple-500/30 text-white' : 'bg-black/30 border-white/5 text-gray-400'
            }`}
            data-testid={`sim-${opt.key}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {action === 'provide_liquidity' && (
        <div>
          <Label className="text-[11px] text-gray-400">Reserve allocation: {allocation}%</Label>
          <input type="range" min={10} max={90} value={allocation} onChange={e => setAllocation(Number(e.target.value))}
            className="w-full mt-1 accent-purple-500" data-testid="sim-allocation"
          />
        </div>
      )}
      <Button onClick={submit} disabled={!action || submitting} className="w-full bg-purple-600 hover:bg-purple-500 gap-2" data-testid="sim-submit-btn">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
        Execute Rebalance
      </Button>
    </div>
  );
}

// ── Mock Vote Component ──
function MockVoteForm({ token, onComplete, profile }) {
  const { toast } = useToast();
  const [vote, setVote] = useState('');
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (profile?.phase2_tasks?.mock_vote) {
    return <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20 flex items-center gap-2">
      <Check className="w-4 h-4 text-green-400" /><span className="text-xs text-green-300">Mock vote submitted</span>
    </div>;
  }

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/genesis-quest/builder/mock-vote`, {
        vote, rationale, proposal_topic: 'Increase Burn-to-Boost base cost by 15%',
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Vote Recorded!', description: `+${data.vxp_earned} vXP` });
      onComplete();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  const wordCount = rationale.trim().split(/\s+/).filter(Boolean).length;

  return (
    <div className="space-y-3 mt-2">
      <div className="p-3 rounded-lg bg-[#0a0a14] border border-white/5">
        <p className="text-xs text-gray-300 font-semibold">Proposal: Increase Burn-to-Boost base cost by 15%</p>
        <p className="text-[11px] text-gray-500 mt-1">This would make boosts more expensive but increase token scarcity.</p>
      </div>
      <div className="flex gap-2">
        {['for', 'against', 'abstain'].map(v => (
          <button key={v} onClick={() => setVote(v)}
            className={`flex-1 p-2 rounded-lg text-xs font-semibold capitalize border transition-all ${
              vote === v ? 'bg-purple-500/10 border-purple-500/30 text-purple-300' : 'bg-black/30 border-white/5 text-gray-500'
            }`} data-testid={`vote-${v}`}
          >{v}</button>
        ))}
      </div>
      <div>
        <Label className="text-[11px] text-gray-400 flex justify-between">
          <span>Your rationale (min 100 words)</span>
          <span className={wordCount >= 100 ? 'text-green-400' : 'text-gray-500'}>{wordCount}/100</span>
        </Label>
        <Textarea value={rationale} onChange={e => setRationale(e.target.value)}
          placeholder="Explain your reasoning..."
          className="mt-1 bg-black/40 border-white/10 text-white text-xs min-h-[100px]"
          data-testid="vote-rationale"
        />
      </div>
      <Button onClick={submit} disabled={!vote || wordCount < 100 || submitting}
        className="w-full bg-purple-600 hover:bg-purple-500 gap-2" data-testid="submit-vote-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Vote className="w-4 h-4" />}
        Submit Mock Vote
      </Button>
    </div>
  );
}

// ── Contribution Form ──
function ContributionForm({ token, onComplete, profile }) {
  const { toast } = useToast();
  const [type, setType] = useState('doc_improvement');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const done = profile?.phase2_tasks?.doc_improvement || profile?.phase2_tasks?.bug_report;
  if (done) {
    return <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20 flex items-center gap-2">
      <Check className="w-4 h-4 text-green-400" /><span className="text-xs text-green-300">Contribution submitted</span>
    </div>;
  }

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/genesis-quest/builder/contribution`, {
        type, title, description,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Submitted!', description: `+${data.vxp_earned} vXP` });
      onComplete();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 mt-2">
      <div className="flex gap-2">
        {[{ key: 'doc_improvement', label: 'Doc Improvement', icon: FileCode }, { key: 'bug_report', label: 'Bug Report', icon: Target }].map(t => (
          <button key={t.key} onClick={() => setType(t.key)}
            className={`flex-1 p-2 rounded-lg text-xs border transition-all flex items-center justify-center gap-1.5 ${
              type === t.key ? 'bg-purple-500/10 border-purple-500/30 text-purple-300' : 'bg-black/30 border-white/5 text-gray-500'
            }`} data-testid={`contrib-${t.key}`}
          ><t.icon className="w-3 h-3" />{t.label}</button>
        ))}
      </div>
      <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="Title"
        className="bg-black/40 border-white/10 text-white text-xs" data-testid="contrib-title"
      />
      <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description (min 50 chars)"
        className="bg-black/40 border-white/10 text-white text-xs min-h-[80px]" data-testid="contrib-desc"
      />
      <Button onClick={submit} disabled={!title || description.length < 50 || submitting}
        className="w-full bg-purple-600 hover:bg-purple-500 gap-2" data-testid="submit-contrib-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
        Submit Contribution
      </Button>
    </div>
  );
}

// ── Listing Proposal ──
function ListingProposalForm({ token, onComplete, profile }) {
  const { toast } = useToast();
  const [chain, setChain] = useState('Base');
  const [pair, setPair] = useState('');
  const [rationale, setRationale] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (profile?.phase3_tasks?.listing_proposal) {
    return <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/20 flex items-center gap-2">
      <Check className="w-4 h-4 text-green-400" /><span className="text-xs text-green-300">Listing proposal submitted</span>
    </div>;
  }

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/api/genesis-quest/catalyst/listing-proposal`, {
        chain, pair, rationale,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast({ title: 'Proposal Submitted!', description: `+${data.vxp_earned} vXP` });
      onComplete();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-3 mt-2">
      <div className="flex gap-2">
        {['Base', 'Solana', 'Ethereum', 'Arbitrum'].map(c => (
          <button key={c} onClick={() => setChain(c)}
            className={`flex-1 p-2 rounded-lg text-xs border transition-all ${
              chain === c ? 'bg-purple-500/10 border-purple-500/30 text-purple-300' : 'bg-black/30 border-white/5 text-gray-500'
            }`} data-testid={`chain-${c}`}
          >{c}</button>
        ))}
      </div>
      <Input value={pair} onChange={e => setPair(e.target.value)} placeholder="Trading pair (e.g., KOJIRU/USDC)"
        className="bg-black/40 border-white/10 text-white text-xs" data-testid="pair-input"
      />
      <Textarea value={rationale} onChange={e => setRationale(e.target.value)} placeholder="Why is this pair underserved? (min 50 chars)"
        className="bg-black/40 border-white/10 text-white text-xs min-h-[80px]" data-testid="proposal-rationale"
      />
      <Button onClick={submit} disabled={!pair || rationale.length < 50 || submitting}
        className="w-full bg-purple-600 hover:bg-purple-500 gap-2" data-testid="submit-proposal-btn"
      >
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Globe className="w-4 h-4" />}
        Submit Listing Proposal
      </Button>
    </div>
  );
}

// ── Leaderboard Row ──
function LeaderboardRow({ entry, isMe }) {
  const cfg = entry.tier ? TIER_CONFIG[entry.tier.name] || TIER_CONFIG.Silver : null;
  const TierIcon = cfg?.icon || Star;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isMe ? 'bg-purple-500/5 border border-purple-500/20' : 'bg-[#0a0a14] border border-white/5'}`}
      data-testid={`leaderboard-rank-${entry.rank}`}
    >
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center font-bold text-xs ${
        entry.rank <= 3 ? 'bg-gradient-to-br ' + (cfg?.color || 'from-gray-500 to-gray-600') + ' text-white' : 'bg-white/5 text-gray-400'
      }`}>
        {entry.rank <= 3 ? <TierIcon className="w-4 h-4" /> : entry.rank}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-white truncate">{entry.display_name} {isMe && <span className="text-purple-400 text-[10px]">(you)</span>}</p>
        <div className="flex items-center gap-2 mt-0.5">
          {entry.phase1_passed && <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[9px] px-1.5 py-0">Scholar</Badge>}
          {entry.phase2_complete && <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[9px] px-1.5 py-0">Builder</Badge>}
          {entry.phase3_complete && <Badge className="bg-orange-500/10 text-orange-400 border-orange-500/20 text-[9px] px-1.5 py-0">Catalyst</Badge>}
        </div>
      </div>
      <div className="text-right">
        <p className="text-sm font-bold text-white">{entry.vxp.toLocaleString()}</p>
        <p className="text-[10px] text-gray-500">vXP</p>
      </div>
      {cfg && (
        <Badge className={`${cfg.bg} ${cfg.text} border ${cfg.border} text-[9px]`}>{entry.tier.name}</Badge>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════
//  MAIN PAGE
// ═════════════════════════════════════════════════
export default function GenesisQuest() {
  const { token, user } = useAuth();
  const { toast } = useToast();
  const [profile, setProfile] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('quest');
  const [referralInput, setReferralInput] = useState('');
  const [copied, setCopied] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      const [me, lb, ov] = await Promise.all([
        axios.get(`${API}/api/genesis-quest/me`, { headers }),
        axios.get(`${API}/api/genesis-quest/leaderboard?limit=50`),
        axios.get(`${API}/api/genesis-quest/overview`),
      ]);
      setProfile(me.data);
      setLeaderboard(lb.data.leaderboard || []);
      setOverview(ov.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const refresh = () => fetchData();

  const copyReferral = () => {
    if (profile?.referral_code) {
      navigator.clipboard.writeText(profile.referral_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const applyReferral = async () => {
    try {
      await axios.post(`${API}/api/genesis-quest/referral/apply`, { referral_code: referralInput }, { headers });
      toast({ title: 'Referral Applied!' });
      refresh();
    } catch (err) {
      toast({ title: 'Failed', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-purple-400" /></div></DashboardLayout>;
  }

  const phases = [
    { done: profile?.phase1_passed && profile?.simulation_complete },
    { done: profile?.phase2_complete },
    { done: profile?.phase3_complete },
  ];
  const totalVxp = profile?.vxp || 0;

  return (
    <DashboardLayout>
      <div className="max-w-5xl mx-auto space-y-6 pb-12">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2" data-testid="quest-title">
                <Flame className="w-6 h-6 text-orange-400" /> The Kojiru Quest
              </h1>
              <p className="text-sm text-gray-500 mt-1">Path to the Architect Tier — Proof of Action</p>
            </div>
            <div className="text-right">
              <p className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-cyan-400" data-testid="total-vxp">
                {totalVxp.toLocaleString()}
              </p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Velocity XP</p>
              {profile?.tier && (
                <Badge className={`mt-1 ${TIER_CONFIG[profile.tier.name]?.bg || ''} ${TIER_CONFIG[profile.tier.name]?.text || ''}`}>
                  {profile.tier.title} (#{profile.rank})
                </Badge>
              )}
            </div>
          </div>
        </motion.div>

        {/* Stat Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            { label: 'Rank', value: `#${profile?.rank || '-'}`, icon: Trophy },
            { label: 'Phases Complete', value: `${phases.filter(p => p.done).length}/3`, icon: Target },
            { label: 'Participants', value: overview?.total_participants || 0, icon: Users },
            { label: 'Whitelist Slots', value: '1,000', icon: Star },
          ].map(s => (
            <Card key={s.label} className="bg-[#07070f] border-white/5">
              <CardContent className="p-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center">
                  <s.icon className="w-4 h-4 text-gray-400" />
                </div>
                <div>
                  <p className="text-lg font-bold text-white">{s.value}</p>
                  <p className="text-[10px] text-gray-500">{s.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Tab Switcher */}
        <div className="flex gap-1 bg-[#07070f] p-1 rounded-lg border border-white/5">
          {[{ key: 'quest', label: 'Quest Phases', icon: Rocket }, { key: 'leaderboard', label: 'Leaderboard', icon: Trophy }].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 p-2.5 rounded-md text-xs font-semibold transition-all flex items-center justify-center gap-2 ${
                tab === t.key ? 'bg-purple-500/10 text-purple-300 border border-purple-500/20' : 'text-gray-500 hover:text-gray-300'
              }`} data-testid={`tab-${t.key}`}
            ><t.icon className="w-3.5 h-3.5" />{t.label}</button>
          ))}
        </div>

        {tab === 'quest' && (
          <div className="space-y-4">
            {/* Phase 1: The Scholar */}
            <PhaseCard phase={1} title="The Scholar" icon={BookOpen}
              description="Pass the technical quiz and complete the Black Swan simulation"
              complete={profile?.phase1_passed && profile?.simulation_complete}
              active={true}
            >
              <div className="space-y-4">
                <ScholarQuiz token={token} onPass={refresh} profile={profile} />
                <div className="border-t border-white/5 pt-3">
                  <p className="text-xs text-gray-300 font-semibold mb-2 flex items-center gap-2">
                    <Zap className="w-3 h-3 text-yellow-400" /> Protocol Simulation: Black Swan Rebalance
                  </p>
                  <BlackSwanSimulation token={token} onComplete={refresh} profile={profile} />
                </div>
              </div>
            </PhaseCard>

            {/* Phase 2: The Builder */}
            <PhaseCard phase={2} title="The Builder" icon={Wrench}
              description="Contribute to the ecosystem — mock vote + code contribution"
              complete={profile?.phase2_complete}
              locked={!profile?.phase1_passed}
              active={profile?.phase1_passed && !profile?.phase2_complete}
            >
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-300 font-semibold mb-1 flex items-center gap-2">
                    <Vote className="w-3 h-3 text-blue-400" /> Governance Beta: Mock Vote
                  </p>
                  <MockVoteForm token={token} onComplete={refresh} profile={profile} />
                </div>
                <div className="border-t border-white/5 pt-3">
                  <p className="text-xs text-gray-300 font-semibold mb-1 flex items-center gap-2">
                    <FileCode className="w-3 h-3 text-green-400" /> Architect's Apprentice: Code Contribution
                  </p>
                  <ContributionForm token={token} onComplete={refresh} profile={profile} />
                </div>
              </div>
            </PhaseCard>

            {/* Phase 3: The Catalyst */}
            <PhaseCard phase={3} title="The Catalyst" icon={Rocket}
              description="Drive growth — listing proposals and curated referrals"
              complete={profile?.phase3_complete}
              locked={!profile?.phase2_complete}
              active={profile?.phase2_complete && !profile?.phase3_complete}
            >
              <div className="space-y-4">
                <div>
                  <p className="text-xs text-gray-300 font-semibold mb-1 flex items-center gap-2">
                    <Globe className="w-3 h-3 text-orange-400" /> Liquidity Scout: Listing Proposal
                  </p>
                  <ListingProposalForm token={token} onComplete={refresh} profile={profile} />
                </div>
                <div className="border-t border-white/5 pt-3">
                  <p className="text-xs text-gray-300 font-semibold mb-2 flex items-center gap-2">
                    <Users className="w-3 h-3 text-cyan-400" /> Curated Referrals
                  </p>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 p-2.5 rounded-lg bg-black/40 border border-white/5 flex items-center gap-2">
                      <Hash className="w-3 h-3 text-gray-500" />
                      <code className="text-xs text-purple-300 font-mono">{profile?.referral_code}</code>
                    </div>
                    <Button size="sm" variant="outline" onClick={copyReferral} className="border-white/10 gap-1" data-testid="copy-referral-btn">
                      {copied ? <Check className="w-3 h-3 text-green-400" /> : <Copy className="w-3 h-3" />}
                    </Button>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1">
                    Scholars referred: <span className="text-cyan-400 font-semibold">{profile?.referred_scholars || 0}</span> — Referrals only count when they pass the Scholar Quiz.
                  </p>
                  {!profile?.referred_by && (
                    <div className="flex gap-2 mt-2">
                      <Input value={referralInput} onChange={e => setReferralInput(e.target.value.toUpperCase())}
                        placeholder="Enter referral code" className="bg-black/40 border-white/10 text-white text-xs font-mono"
                        data-testid="referral-input"
                      />
                      <Button size="sm" onClick={applyReferral} disabled={!referralInput}
                        className="bg-purple-600 hover:bg-purple-500 text-xs" data-testid="apply-referral-btn"
                      >Apply</Button>
                    </div>
                  )}
                </div>
              </div>
            </PhaseCard>
          </div>
        )}

        {tab === 'leaderboard' && (
          <div className="space-y-3" data-testid="leaderboard-section">
            {/* Tier legend */}
            <div className="flex gap-3 flex-wrap">
              {overview?.tiers?.map(t => {
                const cfg = TIER_CONFIG[t.name];
                return (
                  <div key={t.name} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${cfg?.bg || 'bg-white/5'} border ${cfg?.border || 'border-white/10'}`}>
                    <span className={`text-xs font-bold ${cfg?.text || 'text-gray-400'}`}>{t.name}</span>
                    <span className="text-[10px] text-gray-400">#{t.min_rank}-{t.max_rank}</span>
                    <span className="text-[10px] text-gray-500">{t.perks.split('+')[0].trim()}</span>
                  </div>
                );
              })}
            </div>

            {leaderboard.length === 0 ? (
              <div className="p-8 text-center text-gray-500 text-sm">No participants yet. Be the first!</div>
            ) : (
              <div className="space-y-2">
                {leaderboard.map(entry => (
                  <LeaderboardRow key={entry.rank} entry={entry} isMe={entry.display_name === user?.name || entry.display_name === user?.email?.slice(0, 3) + '***'} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
